import { useMemo } from "react";
import { useUserStore } from "../../../stores/user.store";
import { useSettings } from "../../../hooks/useSetting";
import { useBeneficiaryTerm } from "../../../hooks/useBeneficiaryTerm";
import { ISetting } from "../../../domain/setting.interface";

/**
 * The Participant column's header and cell contents (SIMS-8895 item 5).
 *
 * Two account settings decide both, and every tab of the consolidated report
 * that shows a participant has to read them the same way — hence one hook
 * rather than the rule repeated per tab:
 *
 * - `beneficiaryTerms` names the column. The report hard-coded "Participant",
 *   so an account that calls them Members or Clients saw the wrong word in this
 *   report and the right one everywhere else.
 * - `displayBeneficiaryName` decides what the cell holds. When it is off the
 *   account has asked not to surface names, so the cell shows the client id
 *   number instead. This is the account's privacy choice, not a formatting
 *   preference — defaulting the wrong way would show names the account
 *   suppressed, so an unresolved setting is treated as "do not show the name".
 */
export interface ParticipantColumn {
	/** Header label — the account's singular term for a beneficiary. */
	label: string;
	/**
	 * The cell value for one row.
	 *
	 * Returns an empty string rather than a placeholder when there is nothing to
	 * show: SIMS-8895 replaced "(Unknown)", which read as a value the record does
	 * not actually hold.
	 */
	display: (participantName: string, participantCin: string) => string;
}

export function useParticipantColumn(): ParticipantColumn {
	const { userInfo } = useUserStore();
	const { data: settings } = useSettings(userInfo?.id);
	const { singular } = useBeneficiaryTerm();

	const { displayBeneficiaryName } = settings || ({} as ISetting);

	// Memoised on the two values it actually derives from. Callers put this in
	// the dependency list of the `useMemo` that maps their rows, so returning a
	// fresh object every render would rebuild every table row on every render.
	return useMemo(
		() => ({
			label: singular,
			display: (participantName: string, participantCin: string): string =>
				// `=== true`, not truthiness: while the settings request is in flight
				// `displayBeneficiaryName` is undefined, and falling back to showing
				// the name would flash it on screen for accounts that turned it off.
				displayBeneficiaryName === true
					? (participantName ?? "")
					: (participantCin ?? ""),
		}),
		[singular, displayBeneficiaryName],
	);
}
